import React, {useState} from 'react';
import {Dropdown, DropdownToggle, DropdownMenu, DropdownItem} from 'reactstrap';
import {History, Plus, Trash2} from 'lucide-react';
import {toast} from 'react-toastify';
import {useDeleteThread, useGetThreads} from '../api/threadHooks';

interface ThreadListDropdownProps {
    assistantKeyOrId: string;
    selectedThreadId: string | undefined;
    onThreadSelect: (threadId: string) => void;
    onNewThread: () => void;
}

const ThreadListDropdown: React.FC<ThreadListDropdownProps> = ({
    assistantKeyOrId,
    selectedThreadId,
    onThreadSelect,
    onNewThread
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const {data: threads} = useGetThreads(assistantKeyOrId);
    const deleteThread = useDeleteThread();

    const handleDelete = async (threadId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            await deleteThread.mutateAsync(threadId);
            if (threadId === selectedThreadId) {
                onNewThread();
            }
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to delete thread'
            );
        }
    };

    return (
        <div className="d-flex align-items-center gap-1">
            <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                onClick={onNewThread}
                title="New conversation"
            >
                <Plus size={14} />
            </button>
            <Dropdown isOpen={isOpen} toggle={() => setIsOpen(value => !value)}>
                <DropdownToggle
                    color="outline-secondary"
                    size="sm"
                    className="d-flex align-items-center gap-1"
                    title="Conversation history"
                >
                    <History size={14} />
                </DropdownToggle>
                <DropdownMenu end style={{maxHeight: 320, overflowY: 'auto'}}>
                    {(threads?.length ?? 0) === 0 && (
                        <DropdownItem disabled>
                            No conversations yet
                        </DropdownItem>
                    )}
                    {threads?.map(thread => (
                        <DropdownItem
                            key={thread.id}
                            active={thread.id === selectedThreadId}
                            onClick={() => onThreadSelect(thread.id)}
                            className="d-flex align-items-center justify-content-between gap-2"
                        >
                            <span
                                className="text-truncate"
                                style={{maxWidth: 220}}
                            >
                                {thread.name ??
                                    new Date(thread.createdAt).toLocaleString()}
                                {thread.lastPlace && (
                                    <span className="d-block small text-muted text-truncate">
                                        {thread.lastPlace}
                                    </span>
                                )}
                            </span>
                            <Trash2
                                size={14}
                                className="text-danger flex-shrink-0"
                                onClick={event =>
                                    handleDelete(thread.id, event)
                                }
                            />
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </Dropdown>
        </div>
    );
};

export default ThreadListDropdown;
